import { useMemo, useState } from "react";

import { FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";

import Box from "@/components/Box";
import AddressFinderSearch from "@/components/shared/AddressFinderSearch";
import CustomerPage from "@/components/CustomerPage";
import { InternalTextLink } from "@/components/Links";
import { theme } from "@/core/theme";
import { REGENERUS_PRIVACY_URL, REGENERUS_TERMS_OF_USE_URL } from "@/core/urls";
import { CREATE_ORGANISATION_PATIENT_REGISTRATION_MUTATION } from "@/graphql/organisations/mutations";
import { VERIFY_PATIENT_REGISTRATION_TOKEN_QUERY } from "@/graphql/organisations/queries/organisationPatientRegistration";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import useDocTitle from "@/hooks/use-doc-title";
import useScrollToError from "@/hooks/form/useScrollToError";
import useShippingCountries from "@/hooks/useShippingCountries";
import Asterisk from "@/tpo/Asterisk";
import Checkbox from "@/tpo/Checkbox";
import ChevronComponent from "@/tpo/Chevron";
import ControlledFormField from "@/tpo/ControlledFormField";
import ControlledRadioGroup from "@/tpo/ControlledRadioGroup";
import DateInput from "@/tpo/DateInput";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import FormControl from "@/tpo/FormControl";
import { Error } from "@/tpo/InputWrapper";
import NativeSelect from "@/tpo/NativeSelect";
import RequiredFieldsNotice from "@/tpo/partnerDashboard/practitioner/Application/ui/RequiredFieldsNotice";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";
import { getShippingAddressFieldNames } from "@/utils/idealPostcodesAddress";
import { useQuery } from "@apollo/client";
import Loading from "../Loading";

function CannotUseLink() {
  return (
    <CustomerPage bg="haze" includeFooter jumbotronProps={{ bg: "partners", title: "Link not found", color: "dark" }}>
      <Box pt={theme.spacing.section.pt} pb={theme.spacing.section.pb} px={[20, 20, "5.5vw"]}>
        <Box maxWidth={760} mx="auto" fontFamily="gilroyMedium" fontSize={16}>
          This link is not valid. Please contact your practitioner or info@regeneruslabs.com.
        </Box>
      </Box>
    </CustomerPage>
  );
}

function SubmittedConfirmation() {
  return (
    <CustomerPage
      bg="haze"
      includeFooter
      jumbotronProps={{ bg: "partners", title: "Patient Registration", color: "dark" }}
    >
      <Box pt={theme.spacing.section.pt} pb={theme.spacing.section.pb} px={[20, 20, "5.5vw"]}>
        <Box maxWidth={760} mx="auto">
          <Stack bg="white" pt={[60]} pb={[80]} px={[40]} borderRadius={5} gap={16}>
            <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
              Information submitted
            </Box>
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight={1.5}>
              We will let your practitioner know that you have submitted your details and they will
              be in touch once an order has been placed.
            </Box>
          </Stack>
        </Box>
      </Box>
    </CustomerPage>
  );
}

function RegistrationForm({ organisationId, token, prefill, onSuccess }) {
  const shippingCountries = useShippingCountries();

  const isForChild = Boolean(prefill.isForChild);
  const verifiedEmail = prefill.email;
  const isPrefilled = name => Boolean(prefill[name]);

  const transformer = useMemo(
    () => input => {
      const { shippingPhoneNumber, ...rest } = input;
      return {
        ...rest,
        organisation: organisationId,
        token,
        isForChild,
        // Adult mode submits the verified email as the patient email; child mode
        // omits it (the BE will derive a proxy email from the guardian's).
        email: isForChild ? "" : verifiedEmail,
        guardianEmail: isForChild ? verifiedEmail : "",
        shippingPhoneNumber: (shippingPhoneNumber || "").replaceAll(" ", "")
      };
    },
    [organisationId, token, verifiedEmail, isForChild]
  );

  const api = useDjangoGraphqlForm({
    transformer,
    mutation: CREATE_ORGANISATION_PATIENT_REGISTRATION_MUTATION,
    mutationName: "createOrganisationPatientFromRegistrationMutation",
    defaultValues: {
      firstName: prefill.firstName || "",
      lastName: prefill.lastName || "",
      email: isForChild ? "" : verifiedEmail,
      dateOfBirth: prefill.dateOfBirth || "",
      gender: prefill.gender || "",
      shippingName: prefill.shippingName || "",
      shippingLine1: prefill.shippingLine1 || "",
      shippingLine2: prefill.shippingLine2 || "",
      shippingCity: prefill.shippingCity || "",
      shippingPostcode: prefill.shippingPostcode || "",
      shippingCountry: prefill.shippingCountry || "",
      shippingPhoneNumber: prefill.shippingPhoneNumber || "",
      guardianFirstName: prefill.guardianFirstName || "",
      guardianLastName: prefill.guardianLastName || "",
      guardianEmail: verifiedEmail,
      acceptTerms: false
    },
    handleSuccess: onSuccess
  });

  useScrollToError(api);

  return (
    <FormProvider {...api.methods}>
      <CustomerPage
        bg="haze"
        includeFooter
        jumbotronProps={{
          bg: "partners",
          title: "Patient Registration",
          color: "dark"
        }}
      >
        <Box pt={theme.spacing.section.pt} pb={theme.spacing.section.pb} px={[20, 20, "5.5vw"]}>
          <Box maxWidth={760} mx="auto">
          <Stack gap="16px">
            <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
              {prefill.organisationName}
            </Box>
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight={1.5}>
              Please complete your details below to enable your practitioner to place an order with Regenerus Labs on
              your behalf.
            </Box>
          </Stack>
          <Spacer py={20} />
            <Stack
              as="form"
              gap={[20, 20, 40]}
              onSubmit={e => {
                api.methods.clearErrors();
                api.methods.handleSubmit(api.onSubmit)(e);
              }}
            >
              {isForChild && (
                <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                  <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                    Guardian details
                    <Asterisk />
                  </Box>
                  <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight={1.5}>
                    These details are about you, the guardian completing this form on
                    behalf of the patient.
                  </Box>
                  <FormControl
                    as="fieldset"
                    fields={
                      <>
                        <ControlledFormField
                          name="guardianFirstName"
                          label="Guardian first name"
                          Component={FloatingLabelInput}
                          editable={!isPrefilled("guardianFirstName")}
                          showPencilIcon={!isPrefilled("guardianFirstName")}
                        />
                        <ControlledFormField
                          name="guardianLastName"
                          label="Guardian last name"
                          Component={FloatingLabelInput}
                          editable={!isPrefilled("guardianLastName")}
                          showPencilIcon={!isPrefilled("guardianLastName")}
                        />
                        <ControlledFormField
                          name="guardianEmail"
                          label="Guardian email address"
                          Component={FloatingLabelInput}
                          editable={false}
                          showPencilIcon={false}
                        />
                      </>
                    }
                  />
                </Stack>
              )}
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <RequiredFieldsNotice />
                <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                  {isForChild ? "Patient details" : "Your details"}
                  <Asterisk />
                </Box>
                <FormControl
                  as="fieldset"
                  labelProps={{
                    fontFamily: "gilroyBold",
                    fontSize: [28],
                    lineHeight: "130%"
                  }}
                  fields={
                    <>
                      <ControlledFormField
                        name="firstName"
                        label={isForChild ? "Patient first name" : "First name"}
                        Component={FloatingLabelInput}
                        editable={!isPrefilled("firstName")}
                        showPencilIcon={!isPrefilled("firstName")}
                      />
                      <ControlledFormField
                        name="lastName"
                        label={isForChild ? "Patient last name" : "Last name"}
                        Component={FloatingLabelInput}
                        editable={!isPrefilled("lastName")}
                        showPencilIcon={!isPrefilled("lastName")}
                      />
                      {!isForChild && (
                        <ControlledFormField
                          name="email"
                          label="Email address"
                          Component={FloatingLabelInput}
                          editable={false}
                          showPencilIcon={false}
                        />
                      )}
                      <Stack gap="40px">
                        <ControlledFormField
                          name="dateOfBirth"
                          Component={DateInput}
                          disabled={isPrefilled("dateOfBirth")}
                          getDay={dateStr => (dateStr ? dateStr.split("-")[2] : "")}
                          getMonth={dateStr => (dateStr ? dateStr.split("-")[1] : "")}
                          getYear={dateStr => (dateStr ? dateStr.split("-")[0] : "")}
                          constructDate={({ day, month, year }) => `${year}-${month}-${day}`}
                          label="Date of birth"
                          labelProps={{
                            fontFamily: "gilroyBold",
                            fontSize: 18,
                            mt: 20,
                            mb: 20
                          }}
                        />
                        <ControlledRadioGroup
                          Container={Stack}
                          containerProps={{ gap: 20 }}
                          name="gender"
                          label="Gender"
                          disabled={isPrefilled("gender")}
                          labelProps={{
                            fontFamily: "gilroyBold",
                            fontSize: 18,
                            mb: 20
                          }}
                          values={[
                            { label: "Male", value: "Male" },
                            { label: "Female", value: "Female" }
                          ]}
                        />
                      </Stack>
                    </>
                  }
                />
                <Box fontFamily="gilroyBold" fontSize={[22, 22, 28]}>
                  Shipping address
                </Box>
                <Stack gap={20}>
                  {!isPrefilled("shippingLine1") && (
                    <AddressFinderSearch
                      shippingCountries={shippingCountries}
                      fieldNames={getShippingAddressFieldNames({ style: "shippingCamel" })}
                      countryValueType="isoCode"
                    />
                  )}
                  <ControlledFormField
                    name="shippingName"
                    label="Name"
                    Component={FloatingLabelInput}
                    editable={!isPrefilled("shippingName")}
                    showPencilIcon={!isPrefilled("shippingName")}
                  />
                  <ControlledFormField
                    name="shippingLine1"
                    label="Address line 1"
                    Component={FloatingLabelInput}
                    editable={!isPrefilled("shippingLine1")}
                    showPencilIcon={!isPrefilled("shippingLine1")}
                  />
                  <ControlledFormField
                    name="shippingLine2"
                    label="Address line 2"
                    Component={FloatingLabelInput}
                    editable={!isPrefilled("shippingLine2")}
                    showPencilIcon={!isPrefilled("shippingLine2")}
                  />
                  <ControlledFormField
                    name="shippingCity"
                    label="City"
                    Component={FloatingLabelInput}
                    editable={!isPrefilled("shippingCity")}
                    showPencilIcon={!isPrefilled("shippingCity")}
                  />
                  <ControlledFormField
                    name="shippingPostcode"
                    label="Postcode"
                    Component={FloatingLabelInput}
                    editable={!isPrefilled("shippingPostcode")}
                    showPencilIcon={!isPrefilled("shippingPostcode")}
                  />
                  <ControlledFormField
                    name="shippingPhoneNumber"
                    label="Phone number"
                    Component={FloatingLabelInput}
                    editable={!isPrefilled("shippingPhoneNumber")}
                    showPencilIcon={!isPrefilled("shippingPhoneNumber")}
                  />
                  <ControlledFormField
                    name="shippingCountry"
                    Component={NativeSelect}
                    label=""
                    readOnly={isPrefilled("shippingCountry")}
                    styles={{ wrapper: { mt: 20 } }}
                  >
                    <option value="">Select country</option>
                    {shippingCountries.map((country, idx) => (
                      <option key={`${country.isoCode}-${idx}`} value={country.isoCode}>
                        {country.name}
                      </option>
                    ))}
                  </ControlledFormField>
                </Stack>
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <FormControl
                  fields={
                    <>
                      <ControlledFormField
                        label={
                          isForChild ? (
                            <>
                              By submitting this form on behalf of the patient, I confirm that I am
                              their guardian, that I have read and agree to the{" "}
                              <InternalTextLink href={REGENERUS_TERMS_OF_USE_URL}>Terms of Service</InternalTextLink>{" "}
                              and{" "}
                              <InternalTextLink href={REGENERUS_PRIVACY_URL}>Privacy Policy</InternalTextLink>
                              , and I consent to receive health-related news, exclusive promotions, and product updates
                              from Regenerus Lab by email.
                            </>
                          ) : (
                            <>
                              By submitting this form, I confirm that I have read and agree to the{" "}
                              <InternalTextLink href={REGENERUS_TERMS_OF_USE_URL}>Terms of Service</InternalTextLink>{" "}
                              and{" "}
                              <InternalTextLink href={REGENERUS_PRIVACY_URL}>Privacy Policy</InternalTextLink>
                              , and I consent to receive health-related news, exclusive promotions, and product updates
                              from Regenerus Lab by email.
                            </>
                          )
                        }
                        name="acceptTerms"
                        Component={Checkbox}
                        styles={{
                          labelProps: {
                            fontSize: 14,
                            lineHeight: "24px"
                          },
                          wrapperProps: {
                            alignItems: "flex-start"
                          },
                          checkbox: {
                            size: "lg"
                          }
                        }}
                      />
                    </>
                  }
                />
                <ButtonV2
                  type="submit"
                  color="dark"
                  rightIcon={<ChevronComponent />}
                  size={["sm", "sm", "md"]}
                  mx="auto"
                  disabled={api.loading}
                >
                  Submit
                </ButtonV2>
                {!!api.nonFieldError && <Error error={api.nonFieldError} />}
              </Stack>
            </Stack>
          </Box>
        </Box>
      </CustomerPage>
    </FormProvider>
  );
}

export default function OrganisationPatientRegistrationPage() {
  const { organisationId, token } = useParams();
  const [submitted, setSubmitted] = useState(false);

  const { data, loading, error } = useQuery(VERIFY_PATIENT_REGISTRATION_TOKEN_QUERY, {
    variables: { organisationHashId: organisationId, token },
    skip: !organisationId || !token,
    fetchPolicy: "network-only"
  });

  const verifiedToken = data?.verifyPatientRegistrationToken;

  useDocTitle("Patient Registration");

  if (loading) {
    return <Loading />;
  }

  if (error || !verifiedToken || !verifiedToken.organisationName || !verifiedToken.email) {
    return <CannotUseLink />;
  }

  if (submitted) {
    return <SubmittedConfirmation />;
  }

  return (
    <RegistrationForm
      organisationId={organisationId}
      token={token}
      prefill={verifiedToken}
      onSuccess={() => setSubmitted(true)}
    />
  );
}
